import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { CalendarIcon, UsersIcon, CurrencyDollarIcon, TrophyIcon, ArrowLeftIcon, ClockIcon, EyeIcon, PencilIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import ChatPanel from '../components/ChatPanel'
import PoolsApi from '../services/poolsApi'

interface Pool {
  poolId: string
  name: string
  description: string
  type: string
  status: 'active' | 'completed' | 'cancelled'
  startDate: string
  endDate?: string
  currentParticipants: number
  maxParticipants: number
  entryFee: number
  pickDeadline?: string
  createdAt: string
  createdBy: string
  games?: Game[]
  participants?: Participant[]
}

interface Game {
  gameId: string
  homeTeam: string
  awayTeam: string
  homeConference: string
  awayConference: string
  startDate: string
  week: number
  venue: string
  homeScore?: number
  awayScore?: number
  completed: boolean
  status: string
  winner?: string
}

interface Participant {
  userId: string
  name: string
  email: string
  joinedAt: string
  paid: boolean
}

export default function MemberPoolDetailPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const auth0 = useAuth0()
  const { user } = auth0
  const [pool, setPool] = useState<Pool | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isParticipant, setIsParticipant] = useState(false)
  const [joining, setJoining] = useState(false)

  useEffect(() => {
    if (poolId) {
      loadPool()
    }
  }, [poolId])

  const loadPool = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const poolsApi = new PoolsApi(auth0)
      const poolData = await poolsApi.getPool(poolId!)
      setPool(poolData.pool)
      
      // Check if current user is a participant
      const userIsParticipant = poolData.pool.participants?.some(
        (p: Participant) => p.userId === user?.sub
      )
      setIsParticipant(userIsParticipant || false)
      
    } catch (err) {
      console.error('Error loading pool:', err)
      setError('Failed to load pool details')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinPool = async () => {
    if (!poolId) return
    
    try {
      setJoining(true)
      const poolsApi = new PoolsApi(auth0)
      await poolsApi.joinPool(poolId)
      await loadPool() // Reload to update participant status
    } catch (err) {
      console.error('Error joining pool:', err)
      console.error('Full error details:', err)
      
      // Log the response if it's available
      if (err instanceof Error) {
        console.error('Error message:', err.message)
        console.error('Error stack:', err.stack)
      }
      
      setError(`Failed to join pool: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setJoining(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const isPickDeadlinePassed = () => {
    if (!pool?.pickDeadline) return false
    return new Date() > new Date(pool.pickDeadline)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error || !pool) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Pool Not Found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error || 'The requested pool could not be found.'}</p>
          <Link
            to="/pools"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Pools
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/pools"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Pools
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{pool.name}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">{pool.description}</p>
          </div>
          
          <div className="mt-4 sm:mt-0">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(pool.status)}`}>
              {pool.status.charAt(0).toUpperCase() + pool.status.slice(1)}
            </span>
          </div>
        </div>
        <div className="mt-4 lg:hidden">
          <Link
            to={`/chat/pool/${poolId}`}
            state={{
              title: `${pool.name} chat`,
              subtitle: 'Pool participants only',
              locked: !isParticipant,
              lockedMessage: 'Join this pool to chat with other participants.',
            }}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Open Pool Chat
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pool Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Pool Information</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center">
                <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Start Date</p>
                  <p className="text-gray-900 dark:text-white">{formatDate(pool.startDate)}</p>
                </div>
              </div>
              
              {pool.endDate && (
                <div className="flex items-center">
                  <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">End Date</p>
                    <p className="text-gray-900 dark:text-white">{formatDate(pool.endDate)}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center">
                <UsersIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Participants</p>
                  <p className="text-gray-900 dark:text-white">{pool.currentParticipants} / {pool.maxParticipants}</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <CurrencyDollarIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Entry Fee</p>
                  <p className="text-gray-900 dark:text-white">${pool.entryFee}</p>
                </div>
              </div>
              
              {pool.pickDeadline && (
                <div className="flex items-center sm:col-span-2">
                  <ClockIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pick Deadline</p>
                    <p className={`${isPickDeadlinePassed() ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                      {formatDate(pool.pickDeadline)}
                      {isPickDeadlinePassed() && <span className="ml-2 text-sm">(Passed)</span>}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Games List */}
          {pool.games && pool.games.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Games ({pool.games.length})</h2>
              
              <div className="space-y-4">
                {pool.games.map((game) => (
                  <div key={game.gameId} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {game.awayTeam} @ {game.homeTeam}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Week {game.week} • {formatDate(game.startDate)}
                        </p>
                        {game.venue && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">{game.venue}</p>
                        )}
                      </div>
                      
                      {game.completed && (
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Final: {game.awayScore} - {game.homeScore}
                          </p>
                          {game.winner && (
                            <p className="text-xs text-green-600 dark:text-green-400">
                              Winner: {game.winner === 'home' ? game.homeTeam : game.awayTeam}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Participation Status */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Status</h3>
            
            {isParticipant ? (
              <div className="space-y-4">
                <div className="flex items-center text-green-600 dark:text-green-400">
                  <TrophyIcon className="w-5 h-5 mr-2" />
                  <span className="font-medium">You're participating!</span>
                </div>
                
                <div className="space-y-2">
                  <Link
                    to={`/pools/${poolId}/my-picks`}
                    className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <EyeIcon className="w-4 h-4 mr-2" />
                    View My Picks
                  </Link>
                  
                  {!isPickDeadlinePassed() && (
                    <Link
                      to={`/pools/${poolId}/picks`}
                      className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Edit Picks
                    </Link>
                  )}
                  
                  <Link
                    to={`/pools/${poolId}/standings`}
                    className="w-full inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <TrophyIcon className="w-4 h-4 mr-2" />
                    View Standings
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-300">
                  You're not participating in this pool yet.
                </p>
                
                {pool.currentParticipants < pool.maxParticipants && pool.status === 'active' ? (
                  <button
                    onClick={handleJoinPool}
                    disabled={joining}
                    className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {joining ? 'Joining...' : 'Join Pool'}
                  </button>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {pool.status !== 'active' ? 'Pool is not active' : 'Pool is full'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Participants List */}
          {pool.participants && pool.participants.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Participants ({pool.participants.length})
              </h3>
              
              <div className="space-y-3">
                {pool.participants.map((participant) => (
                  <div key={participant.userId} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-300">
                          {participant.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {participant.name || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Joined {new Date(participant.joinedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      {participant.paid ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <CheckCircleIcon className="w-3 h-3 mr-1" />
                          Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Stats</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Games</span>
                <span className="font-medium text-gray-900 dark:text-white">{pool.games?.length || 0}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Completed Games</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {pool.games?.filter(g => g.completed).length || 0}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Paid Participants</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {pool.participants?.filter(p => p.paid).length || 0} / {pool.currentParticipants}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Prize Pool</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${(pool.currentParticipants * pool.entryFee).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <ChatPanel
              kind="pool"
              entityId={poolId}
              title={`${pool.name} chat`}
              subtitle="Pool participants only"
              placeholder="Message this pool…"
              compact
              className="sticky top-24"
              locked={!isParticipant}
              lockedMessage="Join this pool to chat with other participants."
            />
          </div>
        </div>
      </div>
    </div>
  )
}
